import {useEffect, useState} from "react";
import {FiClock} from "react-icons/fi";

import {Icon} from "@/components/icon/Icon.tsx";
import {durationFn, DurationFormatOptions} from "@/lib/format.tsx";
import {MaintenanceOccurrence} from "@/lib/types/maintenance.ts";

import styles from "./ActiveOccurrenceDuration.module.scss";

const shortDuration = (from: string, to: string) => durationFn(from, to, undefined, DurationFormatOptions.SHORT_TOP2);

export const ActiveOccurrenceDuration = ({occurrence}: {occurrence: MaintenanceOccurrence}) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const startsAt = new Date(occurrence.starts_at);

  if (startsAt > now) {
    return (
      <div className={"d-flex gap-1"}>
        <Icon icon={FiClock} removeDivDimensions className="pulse-color-gray-600" />
        <span className="pulseTextBodySmall pulse-color-gray-600">
          Starts in {shortDuration(now.toISOString(), startsAt.toISOString())}
        </span>
      </div>
    );
  }

  if (!occurrence.ends_at) {
    return (
      <div className={"d-flex gap-1"}>
        <Icon icon={FiClock} removeDivDimensions className="pulse-color-gray-600" />
        <span className="pulseTextBodySmall pulse-color-gray-600">
          {shortDuration(startsAt.toISOString(), now.toISOString())} elapsed
        </span>
      </div>
    );
  }

  const endsAt = new Date(occurrence.ends_at);

  if (now > endsAt) {
    return (
      <div className={"d-flex gap-1"}>
        <Icon icon={FiClock} removeDivDimensions className="pulse-color-gray-600" />
        <span className="pulseTextBodySmall pulse-color-gray-600">
          {shortDuration(endsAt.toISOString(), now.toISOString())} since ended
        </span>
      </div>
    );
  }

  const totalDuration = endsAt.getTime() - startsAt.getTime();
  const elapsed = now.getTime() - startsAt.getTime();

  const progressPercent = totalDuration > 0 ? Math.min(100, Math.max(0, (elapsed / totalDuration) * 100)) : 0;

  return (
    <div className={"d-flex flex-column gap-2"}>
      <div className={styles.durationBar}>
        <div className={styles.durationBarFill} style={{width: `${progressPercent}%`}} />
      </div>
      <div className={"d-flex gap-1"}>
        <Icon icon={FiClock} removeDivDimensions className="pulse-color-gray-900" />
        <span className="pulseTextBodySmall pulse-color-gray-900">
          {shortDuration(now.toISOString(), endsAt.toISOString())} left
        </span>
      </div>
    </div>
  );
};
